<template>
  <div>
    <back-nav :to="'/lists/'"></back-nav>

    <div v-if="!error && list">
      <h2 class="list-title black" @click="openAddMembersModal">
        {{ list ? list.title : "Title" }}
        <i class="icon-edit-pencil" v-if="isOwner && list.status === 'active'"></i>
        <i class="icon-info" v-if="!isOwner"></i>
      </h2>
      <div class="list-description" @click="openAddMembersModal">
        {{ list.description }}
      </div>
      <div class="actions">
        <div @click="openAddMembersModal" class="members">
          <div class="member-circle" v-if="isOwner && list.status === 'active'">
            <span class="icon-add"></span>
          </div>
          <members-circles
            class="members-circles-style"
            :membersToShow="membersToShow"
            :tooltips="false"
          ></members-circles>
        </div>

        <div 
          class="light-button" 
          v-if="isOwner && list.status !== 'concluded'"
          @click="concludeSession">
          <a class="title-evaluate bold primary-color">
            {{concludeTitle}}
          </a>
        </div>

        <ima-button 
          v-if="isOwner && list.status === 'concluded'"
          :title="reopenTitle" 
          variant="accent" 
          :act="reopenSession" 
          size="smaller">
        </ima-button>

        <list-details-modal
          v-if="list"
          v-bind:list="list"
          v-bind:listId="listId"
          v-bind:membersNames.sync="membersNames"
          v-bind:isOwner="isOwner"
          v-bind:pageTitle="list ? list.title : 'Title'"
          v-bind:pageDescription="list.description"
        ></list-details-modal>
      </div>

      <div v-if="list.status === 'concluded' && list.conclusion && list.conclusion.length" class="conclusion-box">
        <div class="title-conclude bold">
          Conclusion
        </div>
        <div class="conclusion">
          {{list.conclusion}}
        </div>
      </div>

      <div>
        <div class="list-info">
          <div class="evaluate-ideas">
            <div class="idea-list-header">
              <div class="title-ideas bold">
                Ideas ({{ orderedPublishedIdeas.length }})
              </div>
              <router-link
                v-if="list.status === 'active'"
                :to="{ path: '/lists/' + listId + '/evaluate' }"
                class="title-evaluate bold primary-color"
              >
                <tag-text
                  :title="titleTagNewIdeas"
                  v-if="orderedPublishedIdeas.length < publishedIdeas.length"
                ></tag-text>
              </router-link>
            </div>

            <ima-button 
              class="rateIdeasButton"
              v-if="!emptyStateOrdered && list.status === 'active'"
              :title="rateIdeasTitle" 
              variant="accent" 
              :act="goToEvaluate"
              size="smaller">
            </ima-button>

          </div>
        </div>

        <new-idea
          v-if="newIdeaModeActive"
          :listId="listId"
          :list="list"
          context="brainstorming-session"
        ></new-idea>

        <div class="published-ideas-list">
          <published-idea
            v-for="(idea, index) in orderedPublishedIdeas"
            :key="index"
            :idea="idea"
            :listId="listId"
          ></published-idea>
        </div>

        <add-button
          v-if="list.status === 'active'"
          v-bind:act="displayNewIdea"
          :title="'Create new idea'"
        ></add-button>
      </div>
      <empty-state
        class="list-page simple"
        v-if="emptyState && list.status === 'active'"
        v-bind:callToAction="callToAction"
        v-bind:description="emptyStateDescription"
        v-bind:act="createFirstIdea"
        v-bind:secondary="true"
      ></empty-state>

      <empty-state
        class="list-page simple"
        v-if="emptyState && list.status === 'concluded'"
        callToAction="Reopen session"
        description="There are no ideas in this brainstorming session. Reopen it to collect brilliant ideas!"
        v-bind:act="reopenSession"
        v-bind:secondary="true"
      ></empty-state>

      <empty-state
        class="list-page simple"
        v-if="emptyStateOrdered && list.status === 'active'"
        v-bind:callToAction="'Rate ideas'"
        v-bind:description="'It looks like you didn’t cast your vote. To view ideas, you first need to evaluate them.'"
        v-bind:act="evaluateIdeasInList"
        v-bind:secondary="true"
      ></empty-state>
    </div>

    <empty-state
      v-if="error"
      v-bind:description="'We are sorry, this list does not exist...'"
      :image="'undraw_empty_street.svg'"
    ></empty-state>

    <empty-state
      v-if="!list && !error"
      v-bind:description="'Ohoh, it seems that you are not a member of this list 👀'"
      :image="'undraw_security.svg'"
    ></empty-state>
  </div>
</template>

<script>
import getMembersToShow from "../mixins/getMembersToShow";

import PublishedIdea from "../components/PublishedIdea";
import ListDetailsModal from "../components/ListDetailsModal";
import NewIdea from "../components/NewIdea";
import EmptyState from "../components/EmptyState.vue";
import AddButton from "../components/AddButton.vue";
import MembersCircles from "../components/lists/MembersCircles";
import TagText from "../components/shared/TagText";
import BackNav from "../components/lists/BackNav";
import ImaButton from "../components/shared/ImaButton.vue";

import bus from "@/bus";
import store from "@/store/store";
import router from "../router";

import getIdeasInList from "../gql/ideas/getIdeasInList.gql";
import reopenSession from "../gql/lists/reopenSession.gql";

export default {
  components: {
    PublishedIdea,
    ListDetailsModal,
    NewIdea,
    EmptyState,
    AddButton,
    MembersCircles,
    TagText,
    BackNav,
    ImaButton,
  },
  data() {
    return {
      isEmpty: true,
      newIdeaModeActive: false,
      callToAction: "Create new idea",
      emptyStateDescription:
        "This list seems empty. Publish a new idea and gather feedback from your peers!",
      emptyStateImage: "undraw_blank_canvas.svg",
      listId: this.$route.params.id,
      pageTitle: "In the list",
      publishedIdeas: [],
      error: null,
      concludeTitle: 'Conclude session',
      reopenTitle: 'Reopen',
      rateIdeasTitle: 'Rate ideas',
    };
  },
  apollo: {
    $subscribe: {
      publishedIdeas: {
        query: getIdeasInList,
        variables() {
          return {
            list_id: this.listId,
          };
        },
        update: (data) => data.ideas,
        result(data) {
          this.publishedIdeas = data.data.ideas;
          if (this.publishedIdea && this.publishedIdea.length > 0) {
            this.isEmpty = false;
          }
          bus.$emit("hide-loader");
        },
        error(error) {
          this.error = JSON.stringify(error.message);
          bus.$emit("hide-loader");
        },
      },
    },
  },
  computed: {
    userId() {
      return this.$store.state.user.id;
    },
    list: {
      get: function () {
        if (!this.$store.state.userLists) return;
        for (let i = 0; i < this.$store.state.userLists.length; i++) {
          if (this.$store.state.userLists[i].id == this.listId) {
            return this.$store.state.userLists[i];
          }
        }
        return null;
      },
      set: function (list) {
        let userLists = this.$store.state.userLists;
        userLists[this.listId] = list;
        store.dispatch("fetchUserLists", userLists);
      },
    },
    isOwner() {
      if (!this.list) return;

      if (this.list.owner_id == this.userId) {
        return true;
      } else {
        return false;
      }
    },
    emptyState() {
      return this.publishedIdeas.length === 0 && !this.newIdeaModeActive;
    },
    emptyStateOrdered() {
      return (
        this.orderedPublishedIdeas.length === 0 &&
        this.publishedIdeas.length > 0 &&
        !this.newIdeaModeActive
      );
    },
    membersNames: function () {
      let membersNames = [];
      if (!this.list) return;
      for (let i = 0; i < this.list.users_lists.length; i++) {
        membersNames.push(this.list.users_lists[i].user);
      }
      return membersNames;
    },
    membersToShow: function () {
      return this.membersNames && this.getMembersToShow(this.membersNames);
    },
    orderedPublishedIdeas: function () {
      if (this.publishedIdeas) {
        let orderedPublishedIdeas = [];
        for (let i = 0; i < this.publishedIdeas.length; i++) {
          let showIdea = false;
          let sumPostive = 0;
          let sumNegative = 0;
          let userVoteValue = null;

          if (this.publishedIdeas[i].owner_id == this.userId) {
            showIdea = true;
          }
          for (let j = 0; j < this.publishedIdeas[i].evaluations.length; j++) {
            if (this.publishedIdeas[i].evaluations[j].user_id == this.userId) {
              userVoteValue = this.publishedIdeas[i].evaluations[j].value;
              showIdea = true;
            }

            if (this.publishedIdeas[i].evaluations[j].value == 1) {
              sumPostive = sumPostive + 1;
            }
            if (this.publishedIdeas[i].evaluations[j].value == -1) {
              sumNegative = sumNegative + 1;
            }
          }

          if (showIdea) {
            let newIdea = this.publishedIdeas[i];
            newIdea["sumPostive"] = sumPostive;
            newIdea["sumNegative"] = sumNegative;
            newIdea["userVoteValue"] = userVoteValue;

            orderedPublishedIdeas.push(newIdea);
          }
        }
        return this._.orderBy(
          orderedPublishedIdeas,
          ["sumPostive", "sumNegative"],
          ["desc", "asc"]
        );
      }
      return [];
    },
    titleTagNewIdeas: function () {
      let count =
        this.publishedIdeas.length - this.orderedPublishedIdeas.length;
      if (count == 1) {
        return count.toString() + " new idea";
      } else {
        return count.toString() + " new ideas";
      }
    },
  },
  methods: {
    evaluateIdeasInList: function () {
      router.push({ path: "/lists/" + this.listId + "/evaluate" });
    },
    focusNewIdeaInput: function () {
      setTimeout(() => {
        bus.$emit("focus-textarea");
      }, 100);
    },
    displayNewIdea: function () {
      this.newIdeaModeActive = true;
      this.focusNewIdeaInput();
    },
    createFirstIdea: function () {
      this.isEmpty = false;
      this.newIdeaModeActive = true;
      this.focusNewIdeaInput();
    },
    addNewIdeaModeInactiveEvent: function () {
      bus.$on("new-idea-mode-inactive", () => {
        this.checkPublishIdeaState();
        this.newIdeaModeActive = false;
      });
    },
    openAddMembersModal: function () {
      bus.$emit("add-members-mode");
    },
    ideaCreatedEvent: function () {
      bus.$on("idea-created-in-list", (newIdea) => {
        this.publishedIdeas.push(newIdea);
        this.checkPublishIdeaState();
      });
    },
    checkPublishIdeaState: function () {
      if (this.publishedIdea && this.publishedIdea.length > 0) {
        this.isEmpty = false;
      }
    },
    concludeSession() {
      bus.$emit("open-modal", "ConcludeSessionModal", "session", this.list);
    },
    async reopenSession() {
      const { data } = await this.$apollo.mutate({
        mutation: reopenSession,
        variables: {
          listId: this.list.id,
        }
      }).catch((e) => {
        this.$rollbar.error(e);
        alert("Could not reopen the brainstorming session. Please try again later.");
      })
      if(data) {
        alert("The brainstorming session has been reopened.");
      } else {
        alert("There has been a problem while trying to reopen the brainstorming session. Please try again later.");
      }
    },
    goToEvaluate() {
      this.$router.push( '/lists/' + this.listId + '/evaluate');
    }
  },
  beforeMount() {
    bus.$emit("show-loader", "");
    this.addNewIdeaModeInactiveEvent();
  },
  mixins: [getMembersToShow],
};
</script>

<style scoped lang="scss">
@use '@/styles/theme.scss';

.list-info, 
.conclusion-box {
  margin-top: 20px;
  margin-bottom: 10px;
}

.conclusion {
  white-space: pre-line;
}

.published-ideas-list {
  margin-bottom: 50px;
}

.published-ideas-list .published-idea {
  margin-bottom: 10px;
  padding: 0;
}

.members {
  width: 140px;
  margin-top: 8px;
  cursor: pointer;
}

.evaluate-ideas {
  display: flex;
  justify-content: space-between;
}

.evaluate-ideas a.title-evaluate {
  text-decoration: none;
}

.member-circle {
  display: inline-block;
  border: 1px solid theme.$light-grey-background;
  color: theme.$light-grey-background;
  font-size: 14px;
  height: 25px;
  width: 25px;
  line-height: 25px;
  text-align: center;
  vertical-align: 10px;
  border-radius: 50%;
  vertical-align: baseline;
  transform: scale(1.2);
  margin-right: 8px;
}

.icon-add::before {
  color: theme.$text-placeholder;
  margin: auto;
}

.list-title {
  font-size: 24px;
  font-family: theme.$font-family-strong;
  cursor: pointer;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  margin-bottom: 0px;
}

.list-description {
  font-size: 12px;
  white-space: pre-wrap;
  margin-bottom: 10px;
  color: theme.$text-secondary-color;
  cursor: pointer;
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
  max-width: 400px;
}

.title-ideas,
.title-conclude {
  padding: 6px 0px;
  margin-right: 8px;
}

.tag-new-ideas,
.idea-list-header a.title-evaluate {
  margin-top: auto;
  margin-bottom: auto;
}

.idea-list-header {
  display: flex;
}

.title-ideas,
.title-conclude,
.tag-text {
  display: inline-block;
}

.actions {
  width: 100%;
  display: flex;
  justify-content: space-between;
}

@media (min-width: 768px) {
  .evaluate-ideas {
    justify-content: flex-start;
  }
  .list-title {
    margin-top: 26px;
    overflow: initial;
    white-space: initial;
    text-overflow: initial;
  }
}

.list-title i {
  color: #bbbbbb;
  margin-left: 6px;
  vertical-align: middle;
  font-size: 16px;
}
.rateIdeasButton {
  margin: 0 8px;
}
.empty-state {
  padding-top: 32px;
}
</style>